import { Box, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE } from "../../../theme/tokens";
import type { PbjHppdExplain } from "../../../utils/models";

// The band ladder is scored on 3-decimal HPPD (the BE floor-truncates the displayed value to the
// same precision), so echoing fewer decimals here could show a number that reads as belonging to a
// different band than the one it actually scored in.
const HPPD_DECIMALS = 3;
// A facility sitting at the national case mix divides by ~1.007. Two decimals would render that as
// a flat "1.00" and make the adjustment step look like a no-op.
const DIVISOR_DECIMALS = 4;
const CMI_DECIMALS = 2;

const fixed = (value: number, digits: number): string => value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Hours and resident-days run to five figures over a quarter, so they get a thousands separator and
// no decimals — a "9,842.37" reads as false precision next to a census that is a whole-body count.
const whole = (value: number): string => Math.round(value).toLocaleString();

type Props = {
	// CMS measure id ("1" Total / "2" RN / "3" Weekend). Selects the per-measure wording, matching
	// how `pbj_model.scoring.description.<id>` is already keyed.
	categoryId: string;
	// The measure's own title, reused verbatim as the last line's label so the tooltip closes on the
	// same words the row it hangs off is labelled with.
	categoryTitle: string;
	explain: PbjHppdExplain;
};

/**
 * The body of the Scoring Breakdown's per-measure calculation tooltip (DAT-609): the staff, hours,
 * census and case-mix figures the scorer divided, ending on the adjusted HPPD the row is scored on.
 *
 * Every figure comes from the BE `explain` payload rather than being re-derived from the rendered
 * value, so the arithmetic printed here is the arithmetic that produced the points beside it.
 */
export const HppdExplainTooltipContent: FC<Props> = ({ categoryId, categoryTitle, explain }) => {
	const { t } = useTranslation();

	const hours = whole(explain.hours);
	const censusDays = whole(explain.censusDays);
	const reported = fixed(explain.reportedHppd, HPPD_DECIMALS);
	const divisor = fixed(explain.caseMixDivisor, DIVISOR_DECIMALS);

	const lines: Array<{ key: string; label: string; text: string }> = [
		{
			key: "employees",
			label: t("pbj_model.scoring.explain.employees.label"),
			text: t(`pbj_model.scoring.explain.employees.${categoryId}`, { employees: explain.employees }),
		},
		{
			key: "hours",
			label: t("pbj_model.scoring.explain.hours.label"),
			text: t(`pbj_model.scoring.explain.hours.${categoryId}`, { hours, days: explain.days }),
		},
		{
			key: "census",
			label: t("pbj_model.scoring.explain.census.label"),
			text: t("pbj_model.scoring.explain.census.value", { censusDays, census: explain.averageCensus, days: explain.days }),
		},
		{
			key: "reported",
			label: t("pbj_model.scoring.explain.reported.label"),
			text: t("pbj_model.scoring.explain.reported.value", { hours, censusDays, reported }),
		},
		{
			key: "case-mix",
			label: t("pbj_model.scoring.explain.case_mix.label"),
			text: t("pbj_model.scoring.explain.case_mix.value", {
				divisor,
				facility: fixed(explain.facilityCmi, CMI_DECIMALS),
				national: fixed(explain.nationalCmi, CMI_DECIMALS),
			}),
		},
		{
			key: "adjusted",
			label: categoryTitle,
			text: t("pbj_model.scoring.explain.adjusted.value", { reported, divisor, adjusted: fixed(explain.adjustedHppd, HPPD_DECIMALS) }),
		},
	];

	// Manually-entered prediction hours are a lump sum with no employee behind them, so without
	// saying so the staff count reads as understaffed for the hours shown right above it.
	if (explain.manualHours > 0) {
		lines.splice(2, 0, {
			key: "manual-hours",
			label: t("pbj_model.scoring.explain.manual.label"),
			text: t("pbj_model.scoring.explain.manual.value", { hours: whole(explain.manualHours) }),
		});
	}

	return (
		<Stack spacing={0.5} sx={{ py: 0.5, maxWidth: 320 }} data-testid={`pbj-model-category-${categoryId}-explain`}>
			{lines.map((line) => (
				<Box key={line.key} data-testid={`pbj-model-category-${categoryId}-explain-${line.key}`}>
					<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 700 }}>
						{line.label}
					</Typography>
					<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, fontVariantNumeric: "tabular-nums" }}>
						{` – ${line.text}`}
					</Typography>
				</Box>
			))}
		</Stack>
	);
};
